import { Picture } from '@emico-react/image'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'

import OpenOrClosed from './OpenOrClosed'
import storeImageUrl from './storeImageUrl'
import { SrsStore } from './useSrsStore'
import Link from '../navigation/Link'
import paths from '../paths'
import breakpoints from '../theme/breakpoints'
import Heading from '../typography/Heading'
import Text from '../typography/Text'
import useResolveStoreUrl from '../useResolveStoreUrl'

interface Props {
    store: SrsStore
    storeView: string
    flagshipStore: boolean
}

const StoreComponent = styled.article`
    border: 1px solid #cecece;
    background-color: #fff;
    display: flex;
    flex-flow: column nowrap;
    padding: 0;
`

const Wrapper = styled.div<{ flagshipStore: boolean }>`
    display: flex;
    flex-flow: column nowrap;
    padding: 10px;
    flex: 1;
    order: 2;
    ${(props) => (props.flagshipStore ? 'text-align: center' : '')}
`

const ContentWrapper = styled.div`
    margin-bottom: 10px;
    flex: 1;
`

const StyledOpenOrClosed = styled(OpenOrClosed)`
    margin: 0 0 10px 0;
`

const StoreCard = ({ store, storeView, flagshipStore }: Props) => {
    const { name, gPhone, shopId } = store

    const postal = store?.postal
    const city = store?.city
    const street = store?.street
    const number = store?.number
    const phone = gPhone

    const storeUrl = useResolveStoreUrl(shopId?.toString() ?? undefined)

    if (!name) {
        return null
    }

    const headingStyles =
        !flagshipStore &&
        css`
            margin-bottom: 20px;
        `

    return (
        <StoreComponent>
            <Wrapper flagshipStore={flagshipStore}>
                <ContentWrapper>
                    <Heading variant="h3" element="h2" css={headingStyles}>
                        {name}
                    </Heading>
                    <Text as="p">
                        {street} {number}
                    </Text>
                    <Text as="p">
                        {postal}, {city}
                    </Text>
                    {phone && (
                        <Text as="p">
                            <a href={`tel:${phone}`}>
                                <Text color="dark" as="span">
                                    {phone}
                                </Text>
                            </a>
                        </Text>
                    )}
                </ContentWrapper>

                {store?.gTimes && <StyledOpenOrClosed times={store.gTimes} />}

                {storeUrl && (
                    <Link
                        name="Visit store"
                        category="storelocator.storedetails.visitStore"
                        to={`${paths.storeLocator}/${storeUrl}`}
                        variant="inverted"
                    >
                        {t({
                            id: 'storelocator.storedetails.visitStore',
                            message: `Visit store`,
                        })}
                    </Link>
                )}
            </Wrapper>
            <Picture
                css={css`
                    width: 100%;
                    height: auto;
                `}
                params={{
                    resizingType: 'fill-down',
                }}
                alt={name}
                breakpoints={{
                    [breakpoints.xs]: {
                        url: storeImageUrl(store, storeView, true),
                        width: 386,
                        height: 386,
                    },
                    [breakpoints.md]: {
                        url: storeImageUrl(store, storeView),
                        width: 386,
                        height: 184,
                    },
                }}
                lazy
            />
        </StoreComponent>
    )
}

export default StoreCard
